import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { StakeholderTypeEnumDTO } from 'common/enums/sstk-enum';
import { ApplicationException } from 'common/exceptions/application/ApplicationException';
import { CommunicationTemplate } from 'common/models/CommunicationTemplate';
import { Complaint } from 'common/models/Complaint';
import { Interaction } from 'common/models/Interaction';
import { ProcessedMessages } from 'common/models/ProcessedMessage';
import {
  AutosaveContactDetailResponse,
  CreateAutosaveContactRequest,
  UpdateAutosaveContactRequest,
} from 'common/models/SstkAutosaveContact';
import { Stakeholder } from 'common/models/Stakeholder';
import { getAllStakeholders } from 'common/utils/contactUtils';
import { toIsoWithTimeZone } from 'common/utils/dateUtils';
import { getDirectionForGraphItem } from 'common/utils/graphItemUtils';
import { addToLoaderDetails } from 'common/utils/loaderUtil';
import {
  getDateFromOutlookItem,
  getSubjectFromGraphMessage,
} from 'common/utils/outlookItemUtils';
import { SupportedOfficeItem } from 'common/utils/SupportedOfficeItem';
import CONFIG from 'config';
import * as Api from '../utils/api';
import * as Auth from '../utils/authUtils';
import * as GraphApi from '../utils/graphApi';
import * as ProductAnalytics from '../utils/product-analytics';
import * as UserSettings from '../utils/user-settings';
import * as App from './app';
import { clients } from './app';
import { errorHandler } from './errorHandler';
import * as ItemProperties from './item-properties';
import * as AppSave from './save';

const TAG1 = 'APS3: ';
const TAG2 = 'ATS3: ';
let allNewMessages: MicrosoftGraph.Message[];

export async function createOrUpdateContact(
  firstSaveDate: Date,
  communication: Interaction | Complaint,
  stakeholder: Stakeholder
) {
  const projectID = UserSettings.getProject();
  const clientCode = UserSettings.getClientCode();
  const clientID = clients.find((c) => c.Name === clientCode)?.ClientID || 0;
  const template = communication as CommunicationTemplate;

  const isExistingContact = await Api.doesAutosaveContactExist(
    stakeholder.Email
  );

  if (isExistingContact) {
    const request: UpdateAutosaveContactRequest = {
      lastSyncDate: toIsoWithTimeZone(firstSaveDate),
      email: stakeholder.Email,
    };
    console.log(TAG1, 'Updating existing contact: ', stakeholder.Email);
    await Api.updateAutosaveContact(request);

    return;
  }

  const request: CreateAutosaveContactRequest = {
    clientID: clientID,
    projectID: projectID,
    autosaveStartDate: toIsoWithTimeZone(firstSaveDate),
    lastSyncDate: toIsoWithTimeZone(firstSaveDate),
    email: stakeholder.Email,
    firstName: stakeholder.FirstName,
    lastName: stakeholder.LastName,
    stakeholderType:
      stakeholder.StakeholderType as unknown as StakeholderTypeEnumDTO,
    confidentialityID: template?.Confidential?.Value
      ? +template?.Confidential?.Value
      : undefined,
    assignedToUserID: template?.AssignedTo?.Value
      ? +template?.AssignedTo?.Value
      : undefined,
    priorityID: template?.Priority?.Value
      ? +template?.Priority?.Value
      : undefined,
    statusID: template?.Status?.Value ? +template?.Status?.Value : undefined,
    interactionTypeID: template?.InteractionType?.Value
      ? +template?.InteractionType?.Value
      : undefined,
    tags: template?.SentimentPhrases || [],
    additionalSharedProjectIDs: template?.AdditionalSharedProjectIDs || [],
  };
  console.log(TAG1, 'Create new contact: ', request);
  await Api.saveNewAutosaveContact(request);
}

export async function updateContactLastSync(
  contact: AutosaveContactDetailResponse,
  date: Date
) {
  console.log(TAG1, 'Update contact last sync: ', date);
  if (contact.LastSyncDate > date) {
    console.log(TAG1, 'Setting last sync date: already greater than ', date);
    return;
  }
  contact.LastSyncDate = date;
  await Api.updateAutosaveContact({
    email: contact.Email,
    lastSyncDate: toIsoWithTimeZone(date),
  });
}

export async function deleteAutosaveContact(autosaveContactID: number) {
  console.log(TAG1, 'Deleting autosave contact: ', autosaveContactID);
  await Api.deleteAutosaveContact(autosaveContactID);
}

export async function getAutosaveContacts() {
  if (!Auth.isTokenValid()) {
    await Auth.refreshToken();
  }

  const contacts = await Api.getAutosaveContacts();
  return contacts;
}

export async function isAutosaved(item: SupportedOfficeItem) {
  if (!CONFIG.isGraphApiEnabled) return false;

  if (item.itemType === 'appointment') return false;

  console.log(TAG2, 'Checking if item was autosaved');
  const messageDate = await getDateFromOutlookItem(item);
  // message date will be compared to last sync date, which doesn't include milliseconds,
  // because REST API doesn't include them in date-time fields
  // to make the comparison "fair", messageDate should be stripped of milliseconds
  messageDate.setMilliseconds(0);
  const contacts = await getAutosaveContacts();

  // Get all the email addresses from the email, but:
  // Exclude the user's email address
  // Exclude CC-ed email addresses [includeCc = false]
  const stakeholders = await getAllStakeholders(item, undefined, false);

  // find all autosave contacts for this item
  const itemContacts: AutosaveContactDetailResponse[] = [];
  for (const stakeholder of stakeholders) {
    const contact = contacts.find(
      (c) => c.Email?.toLowerCase() === stakeholder.email?.toLowerCase()
    );
    if (contact) {
      itemContacts.push(contact);
    }
  }

  // if no autosave contacts for this item, then item is not autosaved
  if (itemContacts.length === 0) {
    console.log(TAG2, 'Contacts on SSTKAutoSave', contacts);
    console.log(
      TAG2,
      'The contact is checking -> Message is not supposed to be autosaved'
    );
    return false;
  }

  // check last sync dates for all contacts
  const autosavedForAllContacts = itemContacts.every((c) => {
    console.log(
      TAG2,
      `Checking if item was autosaved for ${c.FirstName} ${c.LastName}`
    );
    console.log(
      TAG2,
      `Last sync date for contact ${c.LastSyncDate}`,
      Number(c.LastSyncDate)
    );
    console.log(TAG2, `Message date is  ${messageDate}`, Number(messageDate));

    return c.LastSyncDate >= messageDate && c.AutosaveStartDate <= messageDate;
  });

  console.log(TAG2, 'Autosaved for all contacts ', autosavedForAllContacts);
  return autosavedForAllContacts;
}

export async function autosaveNewMessages() {
  console.log(TAG2, 'Getting contacts to auto save new messages');

  const contacts = await getAutosaveContacts();

  await prepareMessages(contacts);

  // Filter contacts to include only those with messages
  const contactsWithMessages = contacts.filter((contact) => {
    const emailAddress = contact.Email;
    const hasMsg = hasMessages(emailAddress);

    if (!hasMsg) {
      console.log(TAG2, `Contact with no messages to sync: ${emailAddress}`);
    }

    return hasMsg;
  });

  let messageSaved = false;
  let totalMessages = 0;
  const errorContacts = [];
  const processedMessages: ProcessedMessages = { count: 0 };

  // Calculate the total number of messages
  for (const contact of contactsWithMessages) {
    const messages = getMessages(contact.Email, new Date(contact.LastSyncDate));
    totalMessages += messages.length;
  }

  if (totalMessages > 0) {
    const loaderDetails = 'Autosaving Started';
    addToLoaderDetails({
      text: loaderDetails,
      secondaryText:
        'Please avoid any interruptions, such as changing or opening emails, while autosaving is active.',
    });
    console.log(TAG2, loaderDetails);
  }

  for (const contact of contactsWithMessages) {
    try {
      const result = await autosaveMessagesForContact(
        contact,
        processedMessages,
        totalMessages
      );

      // Update messageSaved if the result is true
      if (result) {
        messageSaved = true;
        console.log(
          TAG2,
          'Completed processing new messages for contact with new messages:',
          contact.Email,
          'result:',
          result
        );
      }
    } catch (error) {
      // Collect error email addresses
      const contactEmail = contact.Email || '(No Email Address)';
      errorContacts.push(contactEmail);

      console.error(
        TAG2,
        `Error processing contact with new messages: ${contactEmail}`,
        error
      );
    }
  }

  if (totalMessages > 0) {
    const loaderDetails = 'Autosaving Completed';
    addToLoaderDetails(loaderDetails);
    console.log(TAG2, loaderDetails);
  }

  if (messageSaved) {
    await ProductAnalytics.trackEvent({ eventName: 'used_email_plugin' });
  }

  if (errorContacts.length > 0) {
    // Show error popup with collected error contacts
    const errorMessage = `Error while autosaving messages for the following contacts:<br><br>${errorContacts.join(
      '<br>'
    )}<br><br>Please contact support.`;
    errorHandler(null, errorMessage);
  }

  // Reset the loader to "Loading"
  addToLoaderDetails(null);
}

async function autosaveMessagesForContact(
  contact: AutosaveContactDetailResponse,
  processedMessages: ProcessedMessages,
  totalMessages: number
) {
  if (!contact.Email) {
    throw new ApplicationException(
      'One of the contacts in autosave contact group is corrupt: ' +
        JSON.stringify(contact)
    );
  }

  console.log(TAG2, 'Processing autosave contact for:', contact.Email);

  if (!isCorrectClientCode(contact)) {
    return false;
  }

  // previous version didn't set customFields.lastSyncDate
  // in this case, use LastModifiedDateTime instead
  // can be removed later
  const lastSyncDate = contact.LastSyncDate;

  console.log(
    TAG2,
    `Searching for new messages from ${contact.Email} since ${lastSyncDate}. Contacts:`,
    contact
  );

  const messages = getMessages(contact.Email, lastSyncDate);
  console.log(TAG2, `Found ${messages.length} messages for ${contact.Email}.`);

  if (messages.length === 0) {
    return false;
  }

  UserSettings.setProject(contact.ProjectID);
  await Auth.refreshToken();

  const stakeholderId = await Api.findStakeholderV2(contact);

  if (!stakeholderId) {
    console.log(TAG2, `${contact.Email} was deleted in ${CONFIG.appName}`);
    console.log(
      TAG2,
      'Deleting contact ',
      contact.FirstName + ' ' + contact.LastName + '-' + contact.Email
    );
    await deleteAutosaveContact(contact.AutosaveContactID);
    throw new ApplicationException(
      `Stakeholder ${contact.Email} was deleted in ${CONFIG.appName}. ` +
        'Add-in will stop auto-saving messages from this stakeholder'
    );
  }

  let messageSaved = false;

  console.log(TAG2, 'messages: ', messages);

  for (const message of messages) {
    console.log(TAG2, 'Process Message: ', message.subject);
    const shouldSave = await shouldSaveMessage(message);
    if (shouldSave) {
      await autosaveMessage(message, contact, stakeholderId);
      await ItemProperties.markItemAsSavedThroughGraph(message);

      messageSaved = true;
      console.log(TAG2, 'Saved as Interaction: ', message.subject);
    }

    // even if message was skipped because it's already saved,
    // we'll need to update last sync date so message would be marked as Autosaved in UI
    await updateContactLastSync(contact, new Date(message.receivedDateTime));

    // Update the progress percentage
    processedMessages.count++;

    const loaderDetails = `Autosaving In Progress (${processedMessages.count} / ${totalMessages})`;
    addToLoaderDetails({
      text: loaderDetails,
      secondaryText:
        'Please avoid any interruptions, such as changing or opening emails, while autosaving is active.',
    });
    console.log(TAG2, loaderDetails);

    // Check if token still valid, if not then refresh the token.
    // This will prevent the subsequent endpoints from failing.
    if (!Auth.isTokenValid()) {
      await Auth.refreshToken();
    }
  }

  return messageSaved;
}

/** Retrieves and saved messages for all contacts in one go
 *  Because requesting them for individual contacts is costly
 */
async function prepareMessages(contacts: AutosaveContactDetailResponse[]) {
  const addressesToDates = new Map<string, Date>();
  for (const contact of contacts) {
    const lastSyncDate = contact.LastSyncDate;

    if (contact.Email) {
      const address = contact.Email;
      addressesToDates.set(address, lastSyncDate);
    }
  }

  console.log(TAG2, 'Getting messages no older than:');
  addressesToDates.forEach((date, address) => {
    console.log(TAG2, `${address}: ${date}`);
  });
  const allMessages = await GraphApi.getMessages(addressesToDates);

  const contactMessagesAscending = allMessages.sort((a, b) => {
    const aDate = new Date(a.receivedDateTime);
    const bDate = new Date(b.receivedDateTime);

    if (aDate > bDate) return 1;
    else if (bDate > aDate) return -1;
    return 0;
  });

  allNewMessages = contactMessagesAscending;
}

/**
 * Filters previously retrieved messages
 */
function getMessages(email: string, lastSyncDate: Date) {
  email = email.toLowerCase();

  const contactMessages = allNewMessages.filter((m) => {
    // REST API might round up time (e.g. 9:00:00 900ms -> 9:00:01)
    // we're adding a second to lastSyncDate to catch messages with round up seconds
    const clone = new Date(lastSyncDate);
    const adjustedLastSyncDate = new Date(
      clone.setSeconds(clone.getSeconds() + 1)
    );

    if (new Date(m.receivedDateTime) <= adjustedLastSyncDate) {
      return false;
    }

    if (
      m.from.emailAddress &&
      m.from.emailAddress.address?.toLowerCase() === email
    ) {
      return true;
    }

    if (!m.toRecipients) return false;

    const recipient = m.toRecipients.find(
      (r) => r.emailAddress.address?.toLowerCase() === email
    );

    if (recipient) return true;
    else return false;
  });

  return contactMessages;
}

function hasMessages(email: string): boolean {
  email = email.toLowerCase();

  const hasMessage = allNewMessages.some((m) => {
    if (
      m.from.emailAddress &&
      m.from.emailAddress.address?.toLowerCase() === email
    ) {
      return true;
    }

    if (!m.toRecipients) return false;

    const recipient = m.toRecipients.find(
      (r) => r.emailAddress.address?.toLowerCase() === email
    );

    return recipient ? true : false;
  });

  return hasMessage;
}

/** Returns false if current domain/client doesn't match the one assigned for this contact */
function isCorrectClientCode(contact: AutosaveContactDetailResponse) {
  // if it's not saved, we'll just try to save the message with current domain/client and assign it to this contact later

  const client = App.clients.find((c) => c.ClientID == contact.ClientID);

  if (UserSettings.getDomain()) {
    if (contact.ClientID && contact.Region) {
      if (!client || contact.Region !== App.userContext.Region) {
        console.log(
          TAG2,
          'Signed in with different domain or client code, skipping this contact'
        );
        return false;
      } else {
        console.log(TAG2, 'Signed in correct domain/client, continuing');
      }
    } else {
      console.log(TAG2, 'Domain/client not set for this contact');
    }
  }

  if (client && client.Name != UserSettings.getClientCode()) {
    UserSettings.setClientCode(client.Name);
  }

  return true;
}

async function shouldSaveMessage(message: MicrosoftGraph.Message) {
  const isMarkedAsSaved = await ItemProperties.isItemSavedThroughGraph(message);
  if (isMarkedAsSaved) {
    console.log(TAG2, 'Message already saved, skipping', message.subject);
    return false;
  }

  const isExcludedPromise = Api.ensureEmailSubjectIsNotExcluded({
    Subject: message.subject,
  });
  const isSavedPromise = isEmailADuplicate(message);
  const isExcluded = await isExcludedPromise;
  const isSavedOnServer = await isSavedPromise;

  if (isExcluded) {
    console.log(
      TAG2,
      'Email subject contains words that are in the exclusion list'
    );
    return false;
  }
  if (isSavedOnServer) {
    console.log(TAG2, 'Email was already saved on the server');
    return false;
  }

  return true;
}

async function isEmailADuplicate(message: MicrosoftGraph.Message) {
  if (CONFIG.allowToSaveMessageAgain) return false;

  const response = await Api.findDuplicateEmail({
    Subject: getSubjectFromGraphMessage(message),
    EmailAddresses: [
      message.from.emailAddress.address,
      ...message.toRecipients.map((to) => to.emailAddress.address),
    ],
    Message: message.body.content,
    DateTime: toIsoWithTimeZone(new Date(message.receivedDateTime)),
  });
  console.log(TAG2, `is Email Duplicate: `, response.IsMatchFound);
  return response.IsMatchFound;
}

async function autosaveMessage(
  message: MicrosoftGraph.Message,
  contact: AutosaveContactDetailResponse,
  stakeholderId: string
) {
  console.log(TAG2, 'message is now saving... StakeholderID: ', stakeholderId);
  console.log(
    TAG2,
    'message is now saving... from Email address: ',
    message?.from?.emailAddress?.address
  );

  await saveInteraction(message, contact, stakeholderId);
}

async function saveInteraction(
  message: MicrosoftGraph.Message,
  contact: AutosaveContactDetailResponse,
  stakeholderId: string
) {
  console.log(TAG2, 'Saving interaction ', message.subject, contact);

  const attachmentsIds = await AppSave.uploadAttachmentsForItemId(
    message.id,
    'message',
    undefined
  );

  const {
    messageBody: sstkInteractionMessageBody,
    largeInlineAttachmentIds: sstkInteractionLargeInlineIds,
  } = await AppSave.updateInlineAttachments(
    message.body.content,
    message.id,
    'message',
    undefined
  );

  message.body.content = sstkInteractionMessageBody;

  const interaction = await getInteractionForAutosave(message, contact);
  AppSave.captureMessageReferenceIdsForGraphMessage(interaction, message);
  const interactionId = await Api.saveInteraction(interaction);
  await Api.linkInteractionToStakeholders(interactionId, [stakeholderId]);
  const allSstkInteractionAttachmentIds = [
    ...attachmentsIds,
    ...sstkInteractionLargeInlineIds,
  ];
  if (allSstkInteractionAttachmentIds.length > 0) {
    await Api.linkInteractionToAttachments(
      interactionId,
      allSstkInteractionAttachmentIds
    );
  }

  await ProductAnalytics.trackEvent({
    eventName: 'new_interaction_saved_from_outlook',
  });

  await ProductAnalytics.trackEvent({
    eventName: 'outlook_message_autosaved_interaction',
  });
}

async function getInteractionForAutosave(
  item: MicrosoftGraph.Message,
  contact: AutosaveContactDetailResponse
) {
  const subject = getSubjectFromGraphMessage(item);
  const direction = await getDirectionForGraphItem(item);
  const dateTime = new Date(item.receivedDateTime);
  const body = item.body.content;

  const communicationDirection = await Api.getDirectionFromServer(direction);

  const interaction: Interaction = {
    Subject: subject,
    Location: null,
    CommunicationDate: toIsoWithTimeZone(dateTime),
    Method: {
      Text: 'Email',
    },
    Direction: communicationDirection,
    Message: body,
    Confidential: {
      Value: contact.ConfidentialityID
        ? contact.ConfidentialityID.toString()
        : '0',
    },
    Duration: null, // there is no autosave for meetings
    InteractionType: {
      Value: contact.InteractionTypeID,
    },
    SentimentPhrases: contact.Tags,
    AdditionalSharedProjectIDs: contact.AdditionalSharedProjectIDs,
  };

  return interaction;
}
